import React from 'react'
import { Tag } from 'antd'

/**
 * The patient's app language, as a short code badge ("ES").
 *
 * Renders NOTHING for English — absent, null, 'en', or anything unrecognised —
 * so it can be dropped in beside any patient name unconditionally and costs an
 * English row no space at all. English is the default and the overwhelming
 * majority (497 of 510 patients on staging have no language set at all), so a
 * badge on every English row would be noise that hides the signal. The badge
 * means "this patient is NOT reading the app in English", which is the thing a
 * clinician actually needs to notice before they call or write.
 *
 * Deliberately NOT the same component as the TEST tag: that one flags
 * disposable data, this one flags a real patient attribute, and giving them one
 * component would couple two unrelated meanings to one colour.
 *
 * `showDefault` makes it render the English case too ("EN"), for the patient
 * detail form where the field is a labelled fact rather than an exception —
 * there, a blank would read as "not set" rather than "English".
 *
 * `flexShrink: 0` matters next to a name in a flex row: without it the tag is
 * what collapses when space runs short, which is backwards.
 *
 * @param {string}  language     the patient's `language` ('en' | 'es' | null)
 * @param {boolean} showDefault  also render when the language is English
 * @param {object}  style        optional extra styles merged over the defaults
 */
const LanguageTag = ({ language, showDefault, style }) => {
  const code = String(language || 'en').toLowerCase()
  const isDefault = code !== 'es'
  if (isDefault && !showDefault) return null
  return (
    <Tag
      color={isDefault ? 'default' : 'blue'}
      style={{
        marginLeft: 8,
        marginRight: 0,
        flexShrink: 0,
        fontSize: '0.7rem',
        lineHeight: '18px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        ...style,
      }}
    >
      {isDefault ? 'EN' : 'ES'}
    </Tag>
  )
}

export default LanguageTag
